// @flow
import * as React from 'react';
import {FC, FunctionComponent, ReactElement, useState} from 'react';
import {
    Avatar,
    Button,
    Card,
    CardContent,
    Container,
    FormControlLabel,
    Grid2 as Grid,
    LinearProgress,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Stack,
    SvgIcon,
    Switch,
    Typography
} from "@mui/material";
import {useHistory, useParams} from "react-router-dom";
import {PregnancyService} from "../../../services/pregnancy-service";
import AnimalCard from "../../AnimalCard";
import {DateTime} from "luxon";
import {TreatmentListItem} from '../TreatmentList/treatment-list-item';
import DateTimeService from "../../../services/date-time-service";
import DatePickerDialog from "../../Common/date-picker-dialog";
import TreatmentService from "../../../services/treatment-service";
import {PregnancyDetailJson, TreatmentListJson} from "../../../api/generated/medical-rest";
import {ReactComponent as MatingDateSvg} from "../../../icons/mating_date.svg"
import SpeedDialIcon from "@mui/material/SpeedDialIcon";
import SpeedDialAction from "@mui/material/SpeedDialAction";
import SpeedDial from "@mui/material/SpeedDial";
import RouteService from "../../../services/route-service";
import {AnimalCreateLocationState} from "../../../pages/animal-create";
import {
    getEstimatedPregnancyDurationDays,
    getPregnancyDurationDays,
    getPregnancyState,
    PregnancyState
} from "./pregnancy-utils";
import {
    BloodtypeOutlined,
    Cake,
    Description,
    EventBusy,
    Flare,
    LockOpen,
    LockPerson,
    SpatialAudioOutlined,
    SportsScoreOutlined
} from "@mui/icons-material";
import ConfirmPregnancyDialog from "./confirm-pregnancy-dialog";
import {ErrorBox, InfoBox} from "../../Common/alerts";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {AnimalService} from "../../../services/animal-service";
import TreatmentType from "../../../api/dtos/treatment-type";
import {PregnancyProgress} from './pregnancy-progress';
import {Widget} from "../../Common";

interface PregnancyDetailsProps {
    pregnancyId?: string
}

interface ChronologyItem {
    dateTime: DateTime,
    component: ReactElement,
}

interface PregnancyListItemProps {
    eventDate: DateTime,
    primary: string,
    icon: ReactElement,
}

const treatmentToChronologyItem = (t: TreatmentListJson): ChronologyItem => ({
    dateTime: t.timestamp,
    component: <TreatmentListItem editable={false} key={t.id} treatment={t}/>
});

const makeChronologyItem = (dateTime: DateTime, attrs: {
    key: string,
    text: string,
    icon: ReactElement
}): ChronologyItem => ({
    dateTime: dateTime,
    component: <ChronologyListItem key={attrs.key}
                                   eventDate={dateTime}
                                   primary={attrs.text}
                                   icon={attrs.icon}/>
});

const pregnancyChronologyItems = (pregnancy: PregnancyDetailJson): ChronologyItem[] => {
    const chronologyItems: ChronologyItem[] = [];

    if (pregnancy.startDate) {
        chronologyItems.push(makeChronologyItem(pregnancy.startDate, {
            key: 'pStart',
            text: 'Trächtigkeitsbeginn',
            icon: <Flare/>
        }));
    }

    if (pregnancy.calculatedEndDate) {
        chronologyItems.push(makeChronologyItem(pregnancy.calculatedEndDate, {
            key: 'pCalcEnd',
            text: 'Errechneter Geburtstermin',
            icon: <SportsScoreOutlined/>
        }));
    }

    if (pregnancy.actualEndDate) {
        if (pregnancy.note.toLowerCase().startsWith("outcome: miscarriage")) {
            chronologyItems.push(makeChronologyItem(pregnancy.actualEndDate, {
                key: 'pActualEnd',
                text: 'Fehlgeburt',
                icon: <EventBusy/>
            }));
        } else if (pregnancy.startDate) {
            chronologyItems.push(makeChronologyItem(pregnancy.actualEndDate, {
                key: 'pActualEnd',
                text: 'Geburt',
                icon: <Cake/>
            }));
        } else {
            chronologyItems.push(makeChronologyItem(pregnancy.actualEndDate, {
                key: 'pActualEnd',
                text: 'Erfolglose Deckung',
                icon: <EventBusy/>
            }));
        }
    }

    if (pregnancy.matingDate) {
        chronologyItems.push(makeChronologyItem(pregnancy.matingDate, {
            key: 'pMating',
            text: 'Deckung',
            icon: <SvgIcon><MatingDateSvg/></SvgIcon>
        }));
    }

    return chronologyItems;
}

const maxDateTime = (a: DateTime, b: DateTime): DateTime => a > b ? a : b;

const usePregnancyDetails = (pregnancyId: string, locked: boolean) => {
    const queryClient = useQueryClient();

    const queryInfo = useQuery({
        queryKey: ['pregnancy', 'details', {id: pregnancyId, locked}],
        enabled: !!pregnancyId,
        queryFn: async () => {
            if (!pregnancyId) {
                return null;
            }

            const pregnancy = await PregnancyService.findPregnancyById(pregnancyId);
            const animalDetails = await AnimalService.loadJsonAnimalsByPanonIds([pregnancy.damId, pregnancy.sireId ?? ''].filter(Boolean));
            const dam = animalDetails.find(a => a.panonIdentifier.id === pregnancy.damId);
            const sire = animalDetails.find(a => a.panonIdentifier.id === pregnancy.sireId);

            if (!dam) {
                return null;
            }

            const filterEndDate: DateTime = pregnancy.actualEndDate || maxDateTime(pregnancy.calculatedEndDate, DateTime.now());
            const treatments = await TreatmentService.loadTreatments({
                animalId: dam.id,
                interval: {start: pregnancy.startDate, end: filterEndDate},
                type: undefined as unknown as string
            });

            const offspring = pregnancy.offspringId
                ? await AnimalService.loadJsonAnimalByPanonId(pregnancy.offspringId)
                : null;

            const chronology: ChronologyItem[] = (locked ? [] : treatments)
                .map(t => treatmentToChronologyItem(t))
                .concat(pregnancyChronologyItems(pregnancy))
                .sort(DateTimeService.sortByDateASC("dateTime"));

            return {
                pregnancy,
                offspring,
                dam,
                sire,
                chronology,
            };
        }
    });

    const invalidateQueries = async () => Promise.all([
        queryClient.invalidateQueries({queryKey: ['pregnancy']}),
        queryClient.invalidateQueries({queryKey: ['pregnancies']}),
    ]);

    const {mutateAsync: recordMiscarriage} = useMutation({
        mutationFn: async (date: DateTime) => PregnancyService.recordMiscarriage(pregnancyId, date),
        onSettled: invalidateQueries,
    });

    const {mutateAsync: confirmPregnancy} = useMutation({
        mutationFn: async ({startDate, calculatedEndDate}: {
            startDate: DateTime,
            calculatedEndDate: DateTime
        }) => PregnancyService.confirmPregnancy(pregnancyId, {startDate, calculatedEndDate}),
        onSettled: invalidateQueries,
    });

    const {mutateAsync: recordUnsuccessfulMating} = useMutation({
        mutationFn: async (date: DateTime) => PregnancyService.recordBirth(pregnancyId, date), // TODO this works, but is semantically awkward
        onSettled: invalidateQueries,
    });

    return {
        ...queryInfo,
        ...queryInfo.data,
        recordMiscarriage,
        recordUnsuccessfulMating,
        confirmPregnancy,
    }
};

const ChronologyListItem: FunctionComponent<PregnancyListItemProps> = ({eventDate, primary, icon}) => {
    return <ListItem>
        <ListItemAvatar>
            <Avatar>
                {icon}
            </Avatar>
        </ListItemAvatar>
        <ListItemText primary={primary} secondary={eventDate.toLocaleString()}/>
    </ListItem>;
}

const getPregnancySuffix = (pregnancyState: PregnancyState | null | undefined) => {
    switch (pregnancyState) {
        case PregnancyState.COMPLETED: return '(abgeschlossen)';
        case PregnancyState.UNSUCCESSFUL: return '(erfolglos)';
        case PregnancyState.PREGNANT: return '(trächtig)';
        case PregnancyState.MATED: return '(gedeckt)';
        default: return '';
    }
};

const PregnancyDetailsContent: FC<{ pregnancyId: string }> = ({pregnancyId}) => {
    const history = useHistory();
    const [openBirthDateDialog, setOpenBirthDateDialog] = useState(false);
    const [openMiscarriageDateDialog, setOpenMiscarriageDateDialog] = useState(false);
    const [openConfirmPregnancyDialog, setOpenConfirmPregnancyDialog] = useState(false);
    const [openUnsuccessfulMatingDialog, setOpenUnsuccessfulMatingDialog] = useState(false);
    const [speedDialOpen, setSpeedDialOpen] = useState<boolean>(false);
    const [locked, setLocked] = useState(true);

    const {
        isLoading,
        isError,
        pregnancy,
        offspring,
        dam,
        sire,
        chronology,
        recordMiscarriage,
        recordUnsuccessfulMating,
        confirmPregnancy,
    } = usePregnancyDetails(pregnancyId, locked);

    if (isLoading) {
        return <LinearProgress/>;
    }

    if (isError) {
        return <ErrorBox>Fehler beim Laden der Trächtigkeit.</ErrorBox>;
    }

    if (!pregnancy || !dam) return null;

    const handleSaveBirthDate = async (date: DateTime) => {
        setOpenBirthDateDialog(false);
        const animalCreateLocationState: AnimalCreateLocationState = {
            pregnancy: {...pregnancy, id: pregnancyId, actualEndDate: date}
        };
        history.push(RouteService.ANIMAL_CREATE, animalCreateLocationState)
    };

    const handleSaveMiscarriage = async (date: DateTime) => {
        await recordMiscarriage(date);
        setOpenMiscarriageDateDialog(false);
    }

    const handleConfirmPregnancy = async (startDate: DateTime, calculatedEndDate: DateTime) => {
        await confirmPregnancy({startDate, calculatedEndDate});
        setOpenConfirmPregnancyDialog(false);
    }

    const handleUnsuccessfulMating = async (date: DateTime) => {
        await recordUnsuccessfulMating(date);
        setOpenUnsuccessfulMatingDialog(false);
    }

    const addTreatment = (treatmentType: TreatmentType) => history.push({
        pathname: RouteService.expand(RouteService.TREATMENT_EDIT, {animalId: dam.id}),
        search: `?treatmentType=${treatmentType}&panonId=${dam.panonIdentifier.id}`,
        state: {
            backLocation: RouteService.expand(RouteService.PREGNANCY_DETAILS, {pregnancyId})
        }
    });

    const sireCard = sire
        ? <AnimalCard data={sire} withImage={true}/>
        : <Card>
            <CardContent>
                <InfoBox>Kein Vater eingetragen</InfoBox>
            </CardContent>
        </Card>;

    const pregnancyState = getPregnancyState(pregnancy);
    const pregnancySuffix = getPregnancySuffix(pregnancyState);

    return (
        <Container>
            <DatePickerDialog title="Geburtsdatum" open={openBirthDateDialog} onSave={handleSaveBirthDate}
                              onCancel={() => setOpenBirthDateDialog(false)}/>
            <DatePickerDialog title="Datum der Fehlgeburt" open={openMiscarriageDateDialog}
                              onSave={handleSaveMiscarriage}
                              onCancel={() => setOpenMiscarriageDateDialog(false)}/>
            <ConfirmPregnancyDialog title="Trächtigkeitsbeginn"
                                    open={openConfirmPregnancyDialog}
                                    dam={dam}
                                    onSave={handleConfirmPregnancy}
                                    onCancel={() => setOpenConfirmPregnancyDialog(false)}/>
            <DatePickerDialog title="Erfolglose Deckung"
                              open={openUnsuccessfulMatingDialog}
                              onSave={handleUnsuccessfulMating}
                              onCancel={() => setOpenUnsuccessfulMatingDialog(false)}/>

            <Stack gap={3} justifyContent="flex-start">
                <Grid container spacing={2}>
                    <Grid size={{xs: 12, md: 6}}>
                        <Stack justifyContent="flex-start">
                            <Typography align="center" variant="h4">Mutter</Typography>
                            <AnimalCard data={dam} withImage={true}/>
                        </Stack>
                    </Grid>
                    <Grid size={{xs: 12, md: 6}}>
                        <Stack justifyContent="flex-start">
                            <Typography align="center" variant="h4">Vater</Typography>
                            {sireCard}
                        </Stack>
                    </Grid>
                </Grid>

                {!!offspring && (
                    <>
                        <Typography align="center" variant="h4">Cria</Typography>
                        <AnimalCard data={offspring} withImage={true}/>
                    </>
                )}

                {!!pregnancy.startDate &&
                    <Stack justifyContent="flex-start">
                        <Typography variant="h4" align="center">Tragezeit</Typography>
                        <Typography align="center">
                            {getPregnancyDurationDays(pregnancy)}/{getEstimatedPregnancyDurationDays(pregnancy)} Tage
                        </Typography>
                        <PregnancyProgress pregnancy={pregnancy}/>
                    </Stack>}

                <Widget displayName={`Trächtigkeitsverlauf ${pregnancySuffix}`}
                        cardMenuEntries={[
                            ...(pregnancyState === PregnancyState.PREGNANT ? [
                                {
                                    actionName: 'Fehlgeburt erfassen',
                                    actionFunction: () => setOpenMiscarriageDateDialog(true),
                                },
                                {
                                    actionName: 'Geburt eintragen',
                                    actionFunction: () => setOpenBirthDateDialog(true),
                                },
                            ] : []),
                            ...(pregnancyState === PregnancyState.MATED ? [
                                {
                                    actionName: 'Erfolglose Deckung',
                                    actionFunction: () => setOpenUnsuccessfulMatingDialog(true),
                                },
                                {
                                    actionName: 'Trächtigkeit bestätigen',
                                    actionFunction: () => setOpenConfirmPregnancyDialog(true),
                                },
                            ] : []),
                        ]}
                        quickActions={[
                            ...(pregnancyState === PregnancyState.PREGNANT ? [
                                {
                                    icon: <EventBusy/>,
                                    action: () => setOpenMiscarriageDateDialog(true),
                                },
                                {
                                    icon: <Cake/>,
                                    action: () => setOpenBirthDateDialog(true),
                                },
                            ] : []),
                            ...(pregnancyState === PregnancyState.MATED ? [
                                {
                                    icon: <EventBusy/>,
                                    action: () => setOpenUnsuccessfulMatingDialog(true),
                                },
                                {
                                    icon: <Flare/>,
                                    action: () => setOpenConfirmPregnancyDialog(true),
                                },
                            ] : []),
                        ]}>
                    <List>
                        <ListItem>
                            <ListItemAvatar>
                                <Avatar>
                                    {locked ? <LockPerson/> : <LockOpen/>}
                                </Avatar>
                            </ListItemAvatar>
                            <FormControlLabel control={
                                <Switch checked={!locked} onChange={ev => setLocked(!ev.target.checked)}/>
                            } label={locked ? 'Behandlungsdaten gesperrt' : 'Behandlungsdaten sichtbar'}/>
                        </ListItem>

                        {!!pregnancy.note && (
                            <ListItem>
                                <ListItemAvatar>
                                    <Avatar>
                                        <Description/>
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText primary="Notiz" secondary={pregnancy.note}/>
                            </ListItem>
                        )}

                        {chronology?.map(c => c.component)}

                        {[PregnancyState.MATED, PregnancyState.PREGNANT].includes(pregnancyState!) && (
                            <ListItem>
                                <ListItemAvatar/>
                                <ListItemText>
                                    <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
                                        {pregnancyState === PregnancyState.PREGNANT && (
                                            <>
                                                <Button startIcon={<EventBusy/>}
                                                        variant="outlined"
                                                        onClick={() => setOpenMiscarriageDateDialog(true)}>
                                                    Fehlgeburt erfassen
                                                </Button>
                                                <Button startIcon={<Cake/>}
                                                        variant="contained"
                                                        onClick={() => setOpenBirthDateDialog(true)}>
                                                    Geburt eintragen
                                                </Button>
                                            </>
                                        )}
                                        {pregnancyState === PregnancyState.MATED && (
                                            <>
                                                <Button startIcon={<EventBusy/>}
                                                        variant="outlined"
                                                        onClick={() => setOpenUnsuccessfulMatingDialog(true)}>
                                                    Erfolglose Deckung
                                                </Button>
                                                <Button startIcon={<Flare/>}
                                                        variant="contained"
                                                        onClick={() => setOpenConfirmPregnancyDialog(true)}>
                                                    Trächtigkeit bestätigen
                                                </Button>
                                            </>
                                        )}
                                    </Stack>
                                </ListItemText>
                            </ListItem>
                        )}
                    </List>
                </Widget>
            </Stack>

            {[PregnancyState.MATED, PregnancyState.PREGNANT].includes(pregnancyState!) && <SpeedDial
                ariaLabel="Schnellzugriff"
                sx={{position: 'fixed', bottom: 16, right: 16}}
                icon={<SpeedDialIcon/>}
                onClose={() => setSpeedDialOpen(false)}
                onOpen={() => setSpeedDialOpen(true)}
                open={speedDialOpen}
            >
                {pregnancyState === PregnancyState.PREGNANT &&
                    <SpeedDialAction
                        key="recordBirth"
                        tooltipTitle="Geburt"
                        tooltipOpen
                        icon={<Cake/>}
                        onClick={() => setOpenBirthDateDialog(true)}
                    />}
                {pregnancyState === PregnancyState.PREGNANT &&
                    <SpeedDialAction
                        key="recordMiscarriage"
                        tooltipTitle="Fehlgeburt"
                        tooltipOpen
                        icon={<EventBusy/>}
                        onClick={() => setOpenMiscarriageDateDialog(true)}
                    />}
                {pregnancyState === PregnancyState.MATED &&
                    <SpeedDialAction
                        key="confirmPregnancy"
                        tooltipTitle="Trächtigkeit bestätigen"
                        tooltipOpen
                        icon={<Flare/>}
                        onClick={() => setOpenConfirmPregnancyDialog(true)}
                    />}
                {pregnancyState === PregnancyState.MATED &&
                    <SpeedDialAction
                        key="unsuccessfulMating"
                        tooltipTitle="Erfolglose Deckung"
                        tooltipOpen
                        icon={<EventBusy/>}
                        onClick={() => setOpenUnsuccessfulMatingDialog(true)}
                    />}
                <SpeedDialAction
                    key="spittest"
                    tooltipTitle="Spucktest hinzufügen"
                    tooltipOpen
                    icon={<BloodtypeOutlined/>}
                    onClick={() => addTreatment(TreatmentType.spittest)}/>
                <SpeedDialAction
                    key="ultrasound"
                    tooltipTitle="Ultraschall hinzufügen"
                    tooltipOpen
                    icon={<SpatialAudioOutlined/>}
                    onClick={() => addTreatment(TreatmentType.ultrasound)}/>
            </SpeedDial>}

        </Container>
    );
};

const PregnancyDetails: FC<PregnancyDetailsProps> = ({pregnancyId}) => {
    const pregId = useParams<PregnancyDetailsProps>().pregnancyId || pregnancyId;

    return pregId
        ? <PregnancyDetailsContent pregnancyId={pregId}/>
        : <ErrorBox>Trächtigkeit nicht gefunden (ID fehlt)</ErrorBox>
};

export default PregnancyDetails;
